import React, { useState, useEffect } from 'react';
import { findingsAPI, departmentsAPI, capaAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Filter,
  Edit,
  Plus,
  Calendar as CalendarIcon,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { format } from 'date-fns';

const FindingsPage = () => {
  const [findings, setFindings] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [capaDialogOpen, setCapaDialogOpen] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [saving, setSaving] = useState(false);

  const [editForm, setEditForm] = useState({
    severity: '',
    observation: '',
    recommendation: '',
    status: '',
  });

  const [capaForm, setCapaForm] = useState({
    owner: '',
    due_date: new Date(),
    status: 'Open',
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter, departmentFilter]);

  const getFilterValue = (value) => value === 'all' ? '' : value;

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (departmentFilter && departmentFilter !== 'all') params.department_id = departmentFilter;

      const [findingsRes, deptsRes, usersRes] = await Promise.all([
        findingsAPI.getAll(params),
        departmentsAPI.getAll(),
        usersAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setFindings(findingsRes.data);
      setDepartments(deptsRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (finding) => {
    setSelectedFinding(finding);
    setEditForm({
      severity: finding.severity,
      observation: finding.observation,
      recommendation: finding.recommendation,
      status: finding.status,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateFinding = async () => {
    if (!selectedFinding) return;

    setSaving(true);
    try {
      await findingsAPI.update(selectedFinding.id, {
        ...selectedFinding,
        ...editForm,
      });
      toast.success('Finding updated');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update finding');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCAPA = async () => {
    if (!selectedFinding) return;

    setSaving(true);
    try {
      await capaAPI.create({
        finding_id: selectedFinding.id,
        owner: capaForm.owner,
        due_date: format(capaForm.due_date, 'yyyy-MM-dd'),
        status: capaForm.status,
      });
      toast.success('CAPA created');
      setCapaDialogOpen(false);
      setCapaForm({ owner: '', due_date: new Date(), status: 'Open' });
    } catch (error) {
      toast.error('Failed to create CAPA');
    } finally {
      setSaving(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      Critical: 'bg-red-100 text-red-800 border-red-200',
      High: 'bg-orange-100 text-orange-800 border-orange-200',
      Medium: 'bg-amber-100 text-amber-800 border-amber-200',
      Low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    };
    return <Badge className={`${styles[severity]} border`}>{severity}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      Open: 'badge-non-compliant',
      'In Progress': 'badge-partial',
      Closed: 'badge-compliant',
    };
    return <Badge className={styles[status] || 'badge-pending'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="findings-page">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Total Findings</p>
              <p className="metric-value text-[#4A5690]">{findings.length}</p>
            </div>
            <AlertTriangle size={24} className="text-[#4A5690]" />
          </div>
        </Card>
        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Open</p>
              <p className="metric-value text-red-500">
                {findings.filter((f) => f.status === 'Open').length}
              </p>
            </div>
            <AlertTriangle size={24} className="text-red-500" />
          </div>
        </Card>
        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">In Progress</p>
              <p className="metric-value text-amber-500">
                {findings.filter((f) => f.status === 'In Progress').length}
              </p>
            </div>
            <AlertTriangle size={24} className="text-amber-500" />
          </div>
        </Card>
        <Card className="metric-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="metric-label">Closed</p>
              <p className="metric-value text-emerald-500">
                {findings.filter((f) => f.status === 'Closed').length}
              </p>
            </div>
            <CheckCircle2 size={24} className="text-emerald-500" />
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-slate-500 mb-1 block">Department</Label>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger data-testid="filter-department">
                  <SelectValue placeholder="All departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              onClick={() => {
                setStatusFilter('all');
                setDepartmentFilter('all');
              }}
            >
              <Filter size={16} className="mr-2" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Findings Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Findings Register
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#4A5690] mx-auto" />
            </div>
          ) : findings.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <AlertTriangle size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No findings found</p>
              <p className="text-sm">Non-compliant observations will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Control</th>
                    <th>Department</th>
                    <th>Severity</th>
                    <th>Observation</th>
                    <th>Recommendation</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.map((finding) => (
                    <tr key={finding.id}>
                      <td>
                        <span className="font-medium text-[#4A5690]">
                          {finding.control_title || finding.control_id}
                        </span>
                      </td>
                      <td>{finding.department_name || 'N/A'}</td>
                      <td>{getSeverityBadge(finding.severity)}</td>
                      <td>
                        <div className="max-w-[200px] truncate">{finding.observation}</div>
                      </td>
                      <td>
                        <div className="max-w-[200px] truncate">{finding.recommendation}</div>
                      </td>
                      <td>{getStatusBadge(finding.status)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditClick(finding)}
                            data-testid={`edit-finding-${finding.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedFinding(finding);
                              setCapaDialogOpen(true);
                            }}
                            data-testid={`add-capa-${finding.id}`}
                          >
                            <Plus size={14} className="mr-1" />
                            CAPA
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Finding</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={editForm.severity}
                onValueChange={(v) => setEditForm({ ...editForm, severity: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={editForm.status}
                onValueChange={(v) => setEditForm({ ...editForm, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Observation</Label>
              <Textarea
                value={editForm.observation}
                onChange={(e) => setEditForm({ ...editForm, observation: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Recommendation</Label>
              <Textarea
                value={editForm.recommendation}
                onChange={(e) => setEditForm({ ...editForm, recommendation: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleUpdateFinding}
                className="flex-1 bg-[#4A5690] hover:bg-[#3D477A]"
                disabled={saving}
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CAPA Dialog */}
      <Dialog open={capaDialogOpen} onOpenChange={setCapaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create CAPA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedFinding && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium">Finding: {selectedFinding.observation}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Action Owner</Label>
              <Select
                value={capaForm.owner}
                onValueChange={(v) => setCapaForm({ ...capaForm, owner: v })}
              >
                <SelectTrigger data-testid="capa-owner-select">
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(capaForm.due_date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={capaForm.due_date}
                    onSelect={(date) => date && setCapaForm({ ...capaForm, due_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setCapaDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCreateCAPA}
                className="flex-1 bg-[#4A5690] hover:bg-[#3D477A]"
                disabled={saving || !capaForm.owner}
                data-testid="submit-capa-btn"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create CAPA
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FindingsPage;
